import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';

// ─── Inline types (fallback if lib/bank/types not available at compile time) ───
interface ReconciliationMatch {
  movimiento_id: string;
  tipo_match: 'exacto' | 'aproximado' | 'regla' | 'manual';
  documento_asociado_id?: string;
  asiento_id?: string;
  regla_aplicada_id?: string;
  confianza: number;
  descripcion: string;
}

// ─── Inline reconciliation engine (fallback) ───
async function runReconciliation(
  supabaseAdmin: ReturnType<typeof getSupabaseAdminClient>,
  bankAccountId: string,
  empresaId: string,
  fechaInicio: string,
  fechaFin: string
): Promise<{ matches: ReconciliationMatch[]; pendientes: number }> {
  const matches: ReconciliationMatch[] = [];

  // 1. Get pending movements in date range
  const { data: movements, error: movError } = await supabaseAdmin
    .from('bank_movements')
    .select('*')
    .eq('bank_account_id', bankAccountId)
    .eq('estado_conciliacion', 'pendiente')
    .gte('fecha_operacion', fechaInicio)
    .lte('fecha_operacion', fechaFin)
    .order('fecha_operacion', { ascending: true });

  if (movError || !movements) {
    return { matches: [], pendientes: 0 };
  }

  // 2. Get active rules for this empresa
  const { data: rules } = await supabaseAdmin
    .from('bank_rules')
    .select('*')
    .eq('empresa_id', empresaId)
    .eq('activa', true)
    .order('prioridad', { ascending: true });

  // 3. Get documents/invoices for matching
  const { data: documentos } = await supabaseAdmin
    .from('documentos')
    .select('id, datos_ia, tipo, estado, fecha_subida')
    .eq('cliente_id', empresaId)
    .in('estado', ['procesado', 'contabilizado']);

  // 4. Try to match each movement
  for (const mov of movements) {
    let matched = false;

    // 4a. Try rule-based matching first
    if (rules && rules.length > 0) {
      for (const rule of rules) {
        if (matchesRule(mov, rule)) {
          matches.push({
            movimiento_id: mov.id,
            tipo_match: 'regla',
            regla_aplicada_id: rule.id,
            confianza: 0.85,
            descripcion: `Regla "${rule.nombre}" aplicada`,
          });
          matched = true;
          break;
        }
      }
    }

    if (matched) continue;

    // 4b. Try exact amount matching with documents
    if (documentos && documentos.length > 0) {
      for (const doc of documentos) {
        const docTotal = extractDocumentTotal(doc);
        if (docTotal !== null && Math.abs(Math.abs(mov.importe) - Math.abs(docTotal)) < 0.01) {
          matches.push({
            movimiento_id: mov.id,
            tipo_match: 'exacto',
            documento_asociado_id: doc.id,
            confianza: 0.95,
            descripcion: `Coincidencia exacta de importe (${mov.importe}€)`,
          });
          matched = true;
          break;
        }
      }
    }

    if (matched) continue;

    // 4c. Try approximate matching (within 5% tolerance)
    if (documentos && documentos.length > 0) {
      for (const doc of documentos) {
        const docTotal = extractDocumentTotal(doc);
        if (docTotal !== null) {
          const diff = Math.abs(Math.abs(mov.importe) - Math.abs(docTotal));
          const tolerance = Math.abs(docTotal) * 0.05;
          if (diff <= tolerance && diff > 0.01) {
            matches.push({
              movimiento_id: mov.id,
              tipo_match: 'aproximado',
              documento_asociado_id: doc.id,
              confianza: 0.65,
              descripcion: `Coincidencia aproximada (diferencia: ${diff.toFixed(2)}€)`,
            });
            matched = true;
            break;
          }
        }
      }
    }
  }

  const pendientes = movements.length - matches.length;
  return { matches, pendientes };
}

// ─── Helper: check if movement matches a rule ───
function matchesRule(
  movement: Record<string, unknown>,
  rule: Record<string, unknown>
): boolean {
  const condiciones = rule.condiciones as Array<{
    campo: string;
    operador: string;
    valor: string | number | [number, number];
  }> | null;

  if (!condiciones || !Array.isArray(condiciones)) return false;

  return condiciones.every((cond) => {
    const fieldValue = movement[cond.campo];
    if (fieldValue === undefined || fieldValue === null) return false;

    const strValue = String(fieldValue).toLowerCase();
    const condValue = String(cond.valor).toLowerCase();

    switch (cond.operador) {
      case 'contiene':
        return strValue.includes(condValue);
      case 'es_igual':
        return strValue === condValue;
      case 'comienza_con':
        return strValue.startsWith(condValue);
      case 'termina_con':
        return strValue.endsWith(condValue);
      case 'mayor_que':
        return Number(fieldValue) > Number(cond.valor);
      case 'menor_que':
        return Number(fieldValue) < Number(cond.valor);
      case 'rango': {
        const [min, max] = cond.valor as [number, number];
        const num = Number(fieldValue);
        return num >= min && num <= max;
      }
      default:
        return false;
    }
  });
}

// ─── Helper: extract total from document ───
function extractDocumentTotal(doc: Record<string, unknown>): number | null {
  const datosIA = doc.datos_ia as Record<string, unknown> | null;
  if (!datosIA) return null;
  const total = datosIA.totalFactura ?? datosIA.importeTotal ?? datosIA.total;
  if (total === undefined || total === null) return null;
  const num = Number(total);
  return isNaN(num) ? null : num;
}

/**
 * Bank Reconciliation API
 * POST /api/bank/reconcile - Start reconciliation
 * PUT  /api/bank/reconcile - Update movement reconciliation status
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  const supabaseAdmin = getSupabaseAdminClient();

  try {
    // ─── POST: Start reconciliation ───
    if (req.method === 'POST') {
      const { bank_account_id, fecha_inicio, fecha_fin } = req.body;

      if (!bank_account_id || !fecha_inicio || !fecha_fin) {
        return res.status(400).json({
          success: false,
          error: 'bank_account_id, fecha_inicio y fecha_fin son obligatorios.',
        });
      }

      // Verify bank account exists and get empresa_id
      const { data: account, error: accountError } = await supabaseAdmin
        .from('bank_accounts')
        .select('id, empresa_id')
        .eq('id', bank_account_id)
        .eq('activa', true)
        .maybeSingle();

      if (accountError || !account) {
        return res.status(404).json({
          success: false,
          error: 'Cuenta bancaria no encontrada o inactiva.',
        });
      }

      // Try to import external engine first, fallback to inline
      let reconcileResult: { matches: ReconciliationMatch[]; pendientes: number };
      try {
        const engineModule = await import('../../../lib/bank/reconciliation-engine');
        if (engineModule.runReconciliation) {
          reconcileResult = await engineModule.runReconciliation(
            supabaseAdmin,
            bank_account_id,
            account.empresa_id,
            fecha_inicio,
            fecha_fin
          );
        } else {
          reconcileResult = await runReconciliation(
            supabaseAdmin,
            bank_account_id,
            account.empresa_id,
            fecha_inicio,
            fecha_fin
          );
        }
      } catch {
        // External engine not available, use inline fallback
        reconcileResult = await runReconciliation(
          supabaseAdmin,
          bank_account_id,
          account.empresa_id,
          fecha_inicio,
          fecha_fin
        );
      }

      // Get total movements count in range
      const { count: totalMovements } = await supabaseAdmin
        .from('bank_movements')
        .select('id', { count: 'exact', head: true })
        .eq('bank_account_id', bank_account_id)
        .gte('fecha_operacion', fecha_inicio)
        .lte('fecha_operacion', fecha_fin);

      // Create reconciliation session record
      const { data: session, error: sessionError } = await supabaseAdmin
        .from('bank_reconciliation_sessions')
        .insert({
          bank_account_id,
          empresa_id: account.empresa_id,
          fecha_inicio,
          fecha_fin,
          movimientos_total: totalMovements ?? 0,
          movimientos_conciliados: reconcileResult.matches.length,
          estado: reconcileResult.pendientes === 0 ? 'completada' : 'en_curso',
          created_by: user.id,
        })
        .select()
        .single();

      if (sessionError) {
        console.error('[API bank/reconcile] Error creating session:', sessionError);
        return res.status(500).json({ success: false, error: sessionError.message });
      }

      // Apply automatic matches (update movements)
      for (const match of reconcileResult.matches) {
        if (match.confianza >= 0.80) {
          await supabaseAdmin
            .from('bank_movements')
            .update({
              estado_conciliacion: 'conciliado',
              documento_asociado_id: match.documento_asociado_id || null,
              asiento_id: match.asiento_id || null,
              regla_aplicada_id: match.regla_aplicada_id || null,
            })
            .eq('id', match.movimiento_id);

          // Update rule usage count
          if (match.regla_aplicada_id) {
            try {
              await supabaseAdmin.rpc('increment_rule_usage', {
                rule_id: match.regla_aplicada_id,
              });
            } catch {
              // Fallback: direct update if RPC not available
              await supabaseAdmin
                .from('bank_rules')
                .update({ ultima_aplicacion: new Date().toISOString() })
                .eq('id', match.regla_aplicada_id);
            }
          }
        }
      }

      return res.status(200).json({
        success: true,
        data: {
          session,
          matches: reconcileResult.matches,
          summary: {
            total: totalMovements ?? 0,
            conciliados: reconcileResult.matches.filter((m) => m.confianza >= 0.80).length,
            sugeridos: reconcileResult.matches.filter((m) => m.confianza < 0.80).length,
            pendientes: reconcileResult.pendientes,
          },
        },
      });
    }

    // ─── PUT: Update movement reconciliation status ───
    if (req.method === 'PUT') {
      const { movimiento_id, estado, documento_asociado_id, asiento_id } = req.body;

      if (!movimiento_id || !estado) {
        return res.status(400).json({
          success: false,
          error: 'movimiento_id y estado son obligatorios.',
        });
      }

      const validEstados = ['pendiente', 'conciliado', 'parcial', 'descartado'];
      if (!validEstados.includes(estado)) {
        return res.status(400).json({
          success: false,
          error: `Estado inválido. Valores permitidos: ${validEstados.join(', ')}`,
        });
      }

      const updateData: Record<string, unknown> = {
        estado_conciliacion: estado,
      };
      if (documento_asociado_id !== undefined) {
        updateData.documento_asociado_id = documento_asociado_id;
      }
      if (asiento_id !== undefined) {
        updateData.asiento_id = asiento_id;
      }

      const { data, error } = await supabaseAdmin
        .from('bank_movements')
        .update(updateData)
        .eq('id', movimiento_id)
        .select()
        .single();

      if (error) {
        console.error('[API bank/reconcile] Error updating movement:', error);
        return res.status(500).json({ success: false, error: error.message });
      }

      if (!data) {
        return res.status(404).json({ success: false, error: 'Movimiento no encontrado.' });
      }

      return res.status(200).json({ success: true, data });
    }

    return res.status(405).json({ success: false, error: 'Method not allowed' });
  } catch (error: any) {
    console.error('[API bank/reconcile] Unexpected error:', error);
    return res.status(500).json({
      success: false,
      error: error.message || 'Error inesperado en conciliación.',
    });
  }
}

export default withAuth(handler);
